import React, { FC, useCallback, useState } from 'react';
import { Button, Tag, EllipsisSpan, Modal, Table, Paging, usePaging, useUpdate } from '@maxtropy/components';
import { PowerOutageAlarmItem } from '../..';
import SubTitle from '@/components/SubTitle';
import {
  apiV2CorsPowerOutageAlarmDetailPost,
  apiV2CorsPowerOutageAlarmAlarmPagePost,
  V2PowerOutageAlarmAlarmPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import {
  AlarmLevel,
  AlarmLevelColorDisplay,
  AlarmLevelDisplay,
  AlarmState,
  AlarmStateColorDisplay,
  AlarmStateDisplay,
} from '@/shared/types';
import dayjs from 'dayjs';
import ArrowImg from '@/assets/images/alarmAnalysis/rightArrow.png';
import PowerFailureImg from '@/assets/images/alarmAnalysis/powerFailure.png';
import styles from './index.module.scss';
import { WarningFilled } from '@ant-design/icons';
import { Spin } from 'antd';
import { Link } from 'react-router-dom';
import { useRequest } from 'ahooks';
import { usePowerOutageAlarmRefresh } from '../hooks';
import { isNil } from 'lodash-es';

export interface IDetailModal {
  visible: boolean;
  record?: PowerOutageAlarmItem;
  onClose: () => void;
}

// 报警记录类型 - 使用非空断言确保类型匹配
type AlarmRecordItem = NonNullable<V2PowerOutageAlarmAlarmPagePostResponse['list']>[number];

const DetailModal: FC<IDetailModal> = props => {
  const { visible, record, onClose } = props;
  const [refreshTime, setRefreshTime] = useState<string>('');
  const [refreshUpdate, refreshUpdateFn] = useUpdate(); // 用于触发刷新

  // 停电报警推送刷新 - 当推送的 id 与当前弹窗的 id 一致时刷新接口
  const pushRefreshTrigger = usePowerOutageAlarmRefresh(visible ? record?.id : undefined);

  // 报警记录分页
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setTotalCount } = pagingInfo;

  // 获取详情数据
  const { loading: detailLoading, data: detailData } = useRequest(
    async () => {
      if (record?.id) {
        const res = await apiV2CorsPowerOutageAlarmDetailPost({ id: record.id });
        setRefreshTime(dayjs().format('YYYY-MM-DD HH:mm:ss'));
        return res;
      }
    },
    {
      refreshDeps: [visible, record, refreshUpdate, pushRefreshTrigger],
      ready: visible && !!record?.id,
    }
  );

  // 获取报警记录列表
  const { loading: alarmLoading, data: alarmRecordsData } = useRequest(
    async () => {
      if (record?.id) {
        const res = await apiV2CorsPowerOutageAlarmAlarmPagePost({
          id: record.id,
          page: pageOffset,
          size: pageSize,
        });
        setTotalCount(res?.total || 0);
        //ts修复
        return (res?.list?.filter(item => item != null) as AlarmRecordItem[]) || [];
      }
      return [];
    },
    {
      refreshDeps: [visible, record, pageOffset, pageSize, refreshUpdate, pushRefreshTrigger],
      ready: visible && !!record?.id,
    }
  );

  // 查看报警详情
  const handleViewAlarmDetail = (alarmId: number) => {
    window.open(`/device/alarm/record/detail/${alarmId}`, '_blank');
  };

  // 手动刷新按钮
  const handleRefresh = useCallback(() => {
    refreshUpdateFn();
  }, [refreshUpdateFn]);

  // 报警记录表格列
  const alarmRecordColumns = [
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      width: 200,
      ellipsis: true,
      render: (v: string, record: AlarmRecordItem) => {
        return (
          <Link to={`/device/manage/device/${record?.deviceId}/detail`} target="_blank">
            <EllipsisSpan value={v} />
          </Link>
        );
      },
    },
    {
      title: '报警等级',
      dataIndex: 'alarmLevel',
      ellipsis: true,
      with: 100,
      render: (v: AlarmLevel) => (
        <Tag border="solid" color={AlarmLevelColorDisplay[v] || 'default'}>
          {AlarmLevelDisplay[v] || '--'}
        </Tag>
      ),
    },
    {
      title: '报警状态',
      dataIndex: 'state',
      ellipsis: true,
      with: 100,
      render: (v: AlarmState) => (
        <Tag border="solid" color={AlarmStateColorDisplay[v] || 'default'}>
          {AlarmStateDisplay[v] || '--'}
        </Tag>
      ),
    },
    {
      title: '报警信息',
      dataIndex: 'alarmName',
      ellipsis: true,
      with: 180,
      render: (v: string) => <EllipsisSpan value={v} />,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as 'right',
      render: (_: unknown, record: AlarmRecordItem) => (
        <Button type="link" onClick={() => handleViewAlarmDetail(record.id!)}>
          查看详情
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="重要停电报警分析"
      open={visible}
      onCancel={onClose}
      size="big"
      bodyScroll
      className={styles.detailModal}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
      ]}
      destroyOnClose
    >
      <Spin spinning={detailLoading}>
        <SubTitle
          title="停电信息"
          style={{ padding: 0 }}
          extraContent={
            <div>
              <label>最新数据刷新时间：</label>
              {refreshTime || '--'}
              <Button wrapStyle={{ marginLeft: 16 }} onClick={handleRefresh}>
                刷新
              </Button>
            </div>
          }
        >
          <div className={styles.baseInfo}>
            <div className={styles.img}>
              <img src={PowerFailureImg} alt="" width={32} />
            </div>
            <div className={styles.info}>
              <label>停电时间</label>
              <div>
                {detailData?.powerOutageTime ? dayjs(detailData?.powerOutageTime).format('YYYY-MM-DD HH:mm:ss') : '--'}
              </div>
            </div>
            <div className={styles.info}>
              <label>第一时间报警的站房</label>
              <div>{detailData?.alarmStation ?? '--'}</div>
            </div>
            <div className={styles.info}>
              <label>第一时间报警的设备（线路）</label>
              <div>{detailData?.alarmDevice ?? '--'}</div>
            </div>
            <div className={styles.info}>
              <label>报警状态</label>
              <Tag
                border="solid"
                color={AlarmStateColorDisplay[detailData?.state as any as AlarmState] || 'default'}
                style={{ width: 'fit-content' }}
              >
                {AlarmStateDisplay[detailData?.state as any as AlarmState] || '--'}
              </Tag>
            </div>
          </div>
        </SubTitle>
        <SubTitle title="停电影响范围">
          <div className={styles.affectedArea}>
            <label>影响区域：</label>
            <div className={styles.affectedAreaContent}>
              {detailData?.affectedArea?.map(item => (
                <Tag border="solid" color="warning" style={{ marginBottom: 8 }}>
                  {item}
                </Tag>
              ))}
            </div>
          </div>
          <div className={styles.affectedArea}>
            <label>影响设备：</label>
            <div className={styles.affectedAreaContent}>
              {detailData?.affectedDevices?.map(item => (
                <Tag border="solid" color="#52E7FF" style={{ marginBottom: 8 }}>
                  {item}
                </Tag>
              ))}
            </div>
          </div>
        </SubTitle>
        <SubTitle title="停电开关/站房信息">
          <div className={styles.affectedArea}>
            <label>停电开关：</label>
            <div className={styles.affectedAreaContent}>
              {detailData?.powerOutageSwitch?.map(item => (
                <Tag border="solid" color="warning" style={{ marginBottom: 8 }}>
                  {item}
                </Tag>
              ))}
            </div>
          </div>
          <div className={styles.affectedArea}>
            <label>停电站房：</label>
            <div className={styles.affectedAreaContent}>
              {detailData?.powerOutageStations?.map(item => (
                <Tag border="solid" color="#52E7FF" style={{ marginBottom: 8 }}>
                  {item}
                </Tag>
              ))}
            </div>
          </div>
        </SubTitle>
        <SubTitle title="停电分析">
          <div className={styles.analysis}>
            {detailData?.powerOutageStations!?.length > 1 ? (
              <>
                <div>
                  <div className={styles.analysisLabel}>先查总站</div>
                  <div>确认上级供电是否正常</div>
                </div>
                <img src={ArrowImg} alt="" width={24} height={24} />
                <div>
                  <div className={styles.analysisLabel}>再查配电站</div>
                  <div>排查开关跳闸情况</div>
                </div>
                <img src={ArrowImg} alt="" width={24} height={24} />

                <div style={{ flex: 1 }}>
                  <div className={styles.analysisLabel}>最后进站房</div>
                  <div>定位具体跳闸线路</div>
                </div>
                <div className={styles.recoveryTime}>
                  <div className={styles.analysisLabel}>
                    {isNil(detailData?.expectedRecoveryTime) ? '--' : `${detailData.expectedRecoveryTime}小时`}
                  </div>
                  <div>预计供能恢复时间</div>
                </div>
                <div className={styles.adviseText}>
                  <WarningFilled size={14} style={{ color: 'var(--mx-warning-color)', marginRight: 4 }} />
                  建议优先检查停电范围最集中的区域。
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className={styles.analysisLabel}>先查配电站</div>
                  <div>确认开关是否跳闸</div>
                </div>
                <img src={ArrowImg} alt="" width={24} height={24} />

                <div style={{ flex: 1 }}>
                  <div className={styles.analysisLabel}>再进站房</div>
                  <div>定位具体跳闸线路</div>
                </div>
                <div className={styles.recoveryTime}>
                  <div className={styles.analysisLabel}>
                    {isNil(detailData?.expectedRecoveryTime) ? '--' : `${detailData.expectedRecoveryTime}小时`}
                  </div>
                  <div>预计供能恢复时间</div>
                </div>
                <div className={styles.adviseText}>
                  <WarningFilled size={14} style={{ color: 'var(--mx-warning-color)', marginRight: 4 }} />
                  故障范围较小，建议优先排查站房内设备。
                </div>
              </>
            )}
          </div>
        </SubTitle>
        <SubTitle title="报警记录">
          <Table
            rowKey="id"
            columns={alarmRecordColumns}
            dataSource={alarmRecordsData}
            loading={alarmLoading}
            pagination={false}
          />
          <Paging pagingInfo={pagingInfo} />
        </SubTitle>
      </Spin>
    </Modal>
  );
};

export default DetailModal;
